/**
 * App Clip URL utilities for building launch URLs
 */

import Utils from '@webcore/core/utils/utils';

export type ContentIdStrategy = 'route' | 'query' | 'hash';

export interface BuildUrlOptions {
  /** How to encode the content ID in the URL. Default: 'hash' */
  contentIdStrategy?: ContentIdStrategy;
  /** Query/hash param name for content ID. Default: 'content' */
  contentIdParam?: string;
  /** Additional query params to include */
  params?: Record<string, string>;
  /** Base URL override (defaults to current origin) */
  baseUrl?: string;
  /** URL to return to on App Clip exit (e.g. current Safari tab URL). Passed as returnUrl query param. */
  returnUrl?: string;
}

// Shared with useAnalyticsIdentity so the id stored here (and forwarded in the
// App Clip URL) is the same one analytics reports — keep them in lockstep.
export const USER_ID_KEY = 'ej_user_id';
export const SESSION_ID_KEY = 'ej_session_id';

function getStoredValue(key: string): string | null {
  try {
    return window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function getCurrentUserId(): string {
  return getStoredValue(USER_ID_KEY) || Utils.UUIDShort();
}

/**
 * Get or create a session ID from sessionStorage
 */
function getSessionId(): string {
  try {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = Utils.UUIDShort();
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return Utils.UUIDShort();
  }
}

/**
 * Build a target URL with content ID
 */
export function buildTargetUrl(contentId: string, options: BuildUrlOptions = {}): string {
  const { 
    contentIdStrategy = 'hash', 
    contentIdParam = 'content',
    params = {},
    baseUrl,
  } = options;
  
  const url = new URL(baseUrl || window.location.origin);
  
  // Set content ID based on strategy
  switch (contentIdStrategy) {
    case 'route':
      url.pathname = `/${contentId}`;
      break;
    case 'query':
      url.searchParams.set(contentIdParam, contentId);
      break;
    case 'hash':
      url.hash = contentId;
      break;
  }
  
  // Return URL for App Clip exit
  if (options.returnUrl) {
    url.searchParams.set('returnUrl', options.returnUrl);
  }
  
  // Add additional params
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  
  return url.toString();
}

/**
 * Build an App Clip URL for launching with specific content.
 * Uses the current site URL with content ID as hash by default.
 */
export function buildAppClipUrl(contentId: string, options: BuildUrlOptions = {}): string {
  const optionsWithTimestamp: BuildUrlOptions = {
    ...options,
    contentIdStrategy: options.contentIdStrategy ?? 'hash',
    returnUrl: options.returnUrl,
    params: {
      ...options.params,
      userId: options.params?.userId ?? getCurrentUserId(),
      sessionId: options.params?.sessionId ?? getSessionId(),
      _t: Date.now().toString(),
    },
  };
  
  const targetUrl = buildTargetUrl(contentId, optionsWithTimestamp);
  const encodedTarget = encodeURIComponent(targetUrl);
  const playUrl = `https://play.eyejack.xyz/link/?url=${encodedTarget}`;
  const encodedPlayUrl = encodeURIComponent(playUrl);
  const appClipUrl = `https://appclip.apple.com/id?p=com.eyejackapp.EyeJack.Clip&link=${encodedPlayUrl}`;

  return appClipUrl;
}

/**
 * Get current URL with hash and fromAr param for return navigation.
 * Used when launching App Clip to ensure Safari returns to the correct section.
 */
export function getReturnUrl(contentId?: string): string {
  const url = new URL(window.location.href);
  if (contentId) {
    url.hash = contentId;
  }
  url.searchParams.set('fromAr', '1');
  return url.toString();
}
